// The planner in the calendar (doc/feature/planner.md): a person's to-dos for a week and their
// days' names, circles and journal. Weeks are their Monday (`YYYY-MM-DD`); the server says which
// week is the current one on the person's clock (`this_week`).

import { dayLabel, daysBetween } from './calendar.js'

const shortDay = (day) => dayLabel(day, { month: 'short', day: 'numeric' })

/** "This week", "Next week", "Last week", "Week of Nov 9". */
function weekName(week, thisWeek) {
  const ahead = daysBetween(thisWeek, week) / 7
  if (ahead === 0) return 'This week'
  if (ahead === 1) return 'Next week'
  if (ahead === -1) return 'Last week'
  return `Week of ${shortDay(week)}`
}

/** The to-do row's title: "Sometime this week", "Sometime next week", "Sometime in the week of Nov 9". */
export function weekTitle(week, thisWeek) {
  const name = weekName(week, thisWeek)
  return name.startsWith('Week') ? `Sometime in the week of ${shortDay(week)}` : `Sometime ${name.toLowerCase()}`
}

/** Where a carried to-do comes from, seen from the week listing it: "from last week", "from Oct 19". */
export function fromWeek(week, listWeek) {
  return daysBetween(week, listWeek) === 7 ? 'from last week' : `from ${shortDay(week)}`
}

/** How far a list is done: "2 of 5 done", '' for an empty list. */
export function todoCounts(items) {
  const done = items.filter((item) => item.done).length
  return items.length ? `${done} of ${items.length} done` : ''
}

/**
 * A to-do dragged from index `from` to the gap before index `to` (`items.length` for the end):
 * the list as it will be and the `before` ref `planner/todo_move` takes (null: last), or null
 * when it stays where it was.
 */
export function reorder(items, from, to) {
  if (from < 0 || to === from || to === from + 1) return null
  const rest = items.filter((_, index) => index !== from)
  const at = to > from ? to - 1 : to
  rest.splice(at, 0, items[from])
  return { items: rest, before: rest[at + 1]?.ref || null }
}

/** `planner/days` items by day. */
export function dayMarks(items) {
  return Object.fromEntries((items || []).map((item) => [item.day, item]))
}

/** A markdown line as text to skim: no list, heading or quote marker, no emphasis, links as their text. */
export function plainLine(line) {
  return (line || '')
    .replace(/^\s*(?:[*+-]|\d+[.)]|#{1,6}|>)\s+/, '')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/(\*\*|__|~~|`)(.+?)\1/g, '$2')
    .replace(/(^|\W)[*_](\S(?:.*?\S)?)[*_](?=\W|$)/g, '$1$2')
}

/**
 * A planner hit of `calendar/search` as a result row: `{ key, kind, title, when, detail, done,
 * view, date }`, where `view` and `date` are where a click goes. A to-do of Upcoming
 * goes to the week that lists it (the current week for a carried one), of Past to its own week.
 */
export function plannerHit(hit, thisWeek, past) {
  if (hit.kind === 'todo' && hit.todo) {
    const todo = hit.todo
    const week = past || todo.week >= thisWeek ? todo.week : thisWeek
    const carried = week !== todo.week ? fromWeek(todo.week, week) : ''
    return {
      key: `todo:${todo.ref}`,
      kind: 'todo',
      title: todo.text,
      when: [weekTitle(week, thisWeek), carried].filter(Boolean).join(', '),
      detail: todo.done ? 'Done' : '',
      done: todo.done,
      view: 'week',
      date: week,
    }
  }
  if (hit.kind === 'day' && hit.day) {
    const day = hit.day
    return {
      key: `day:${day.day}`,
      kind: 'day',
      title: day.label || 'Journal',
      when: dayLabel(day.day, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }),
      detail: plainLine(hit.snippet),
      done: false,
      view: 'day',
      date: day.day,
    }
  }
  return null
}
